/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { DataApiService } from './data-api-service';
import { ContextualRequestService } from './contextual-request.service';
import { QuantApiService } from '../quant-api/quant-api.service';
import { EntityInfoService } from '../metadata-api/entity-info.service';
import {
  EventsRequestBody,
  CollapsedEventsResponse,
} from '../../models/events.interface';
import { AuthService } from 'src/app/service/auth.service';
import { ConfigService } from 'src/app/service/config.service';
import { ParamsBuilder } from '../../utils/params-builder';
import getScoreTypeDetails from '../../utils/score-type-details';

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  private get project(): string {
    return this.config.appConfig.routing.apiproj;
  }

  constructor(
    private dataApiService: DataApiService,
    private contextualRequest: ContextualRequestService,
    private quantApiService: QuantApiService,
    private entityInfoService: EntityInfoService,
    private auth: AuthService,
    private config: ConfigService,
  ) {}

  getEventsTimeSeries(
    body: EventsRequestBody,
  ): Observable<CollapsedEventsResponse> {
    return this.dataApiService.post<CollapsedEventsResponse>(
      'events',
      body,
      {},
      'v5',
    );
  }

  /**
   * Top sources for an entity/portfolio/query, enriched with entity names.
   * Uses v2 endpoint.
   */
  getTopSources(iparams: any, size: number): Observable<any> {
    const resolvedParams = JSON.parse(JSON.stringify(iparams));
    const params = ParamsBuilder.build(
      resolvedParams,
      ['size', ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS],
      this.project,
    );
    if (size) {
      params['size'] = size;
    }

    return this.contextualRequest
      .request('top_sources', resolvedParams, params, 'v2')
      .pipe(
        mergeMap((respArg) => {
          const resp = JSON.parse(JSON.stringify(respArg));
          const sourcesKeys: string[] = [];
          resp.top_sources.forEach((source: any) => {
            sourcesKeys.push(source.source.type + ':' + source.source.alpha_id);
          });
          return this.entityInfoService.getEntitiesInfo(sourcesKeys).pipe(
            map((result: any) => {
              resp.top_sources.forEach((source: any, index: number) => {
                const key =
                  resp['top_sources'][index]['source'].type +
                  ':' +
                  resp['top_sources'][index]['source'].alpha_id;
                if (Object.prototype.hasOwnProperty.call(result, key)) {
                  resp['top_sources'][index]['source'].name = result[key].name;
                }
              });
              return resp;
            }),
          );
        }),
        catchError(() => of({ top_sources: [] })),
      );
  }

  /**
   * Get score event weights for filtering event time series.
   * score_ts_weights uses all/all endpoint (Data API).
   * bull_bear uses all/all endpoint (Quant API trading/v3).
   */
  getScoreWeights(_iparams: any, score: string): Observable<string[]> {
    const scoreDetails = getScoreTypeDetails(score);

    if (
      scoreDetails.apiType === null &&
      scoreDetails.scoreKey === 'sentiment'
    ) {
      return of([]);
    }

    if (scoreDetails.scoreKey === 'bb') {
      return this.quantApiService
        .get<any>('all/all/bull_bear_score_ts_weights', null, 'trading/v3')
        .pipe(map((res) => Object.values(res['weights'].event_id) as string[]));
    }

    if (scoreDetails.scoreKey) {
      const weightParams: any = { score_type: scoreDetails.scoreKey };
      if (score === 'esg') {
        weightParams['disable_industry_materiality'] = true;
      }
      return this.dataApiService
        .get<any>('all/all/score_ts_weights', weightParams)
        .pipe(map((res) => Object.values(res['weights'].event_id) as string[]));
    }

    const customScoreObj = this.auth.scorelabSettings?.customScores?.find(
      (el: any) => el.uid === score,
    );
    if (customScoreObj?.event_weights) {
      return of(
        customScoreObj.event_weights.map((el: any) => el.event_id as string),
      );
    }

    return of([]);
  }
}
